import { apiClient } from './api';
import { callV3 } from '../api/envelope';
import type { VideoProvidersCatalogResponse } from '../domain';

/**
 * Per-provider health row from `GET /api/v3/providers/health`.
 * Mirrors backend `ProviderHealth` in `ProvidersController.cs`.
 */
export interface ProviderHealth {
  /** Display name from the provider's `ProviderName` getter. */
  name: string;
  /** `'llm'` or `'image'`. */
  kind: 'llm' | 'image' | string;
  /** `'ok'` or `'down'`. */
  status: 'ok' | 'down' | string;
  latencyMs: number;
  modelCount: number;
  error?: string | null;
}

export interface ProvidersHealthResponse {
  llm: ProviderHealth[];
  image: ProviderHealth[];
  checkedAt: string;
}

export interface OllamaInstalledModel {
  name: string;
  sizeBytes: number;
  modifiedAt: string;
}

export interface OllamaInstalledModelsResponse {
  models: OllamaInstalledModel[];
  daemonReachable: boolean;
}

/**
 * Single model entry surfaced by `/api/v3/providers/catalog`. Mirrors
 * `ProviderCatalogModel` on the BE — `id` is what the FE sends back as
 * `model` on operations and as `context.model` on chat/agent invocations.
 */
/**
 * What a model costs, in whichever unit it bills by — image models charge per
 * image, LLMs per million tokens. Exactly one shape is populated; the other
 * side stays undefined, so the unit is unambiguous without inferring it from
 * the provider kind. Absent entirely when the provider publishes no price
 * (locally-hosted Ollama models are free).
 */
export interface ProviderCatalogPricing {
  /** Image models: cost of one generated image. */
  pricePerImage?: number | null;
  /** LLM models: cost per 1M input tokens. */
  inputTokensPer1M?: number | null;
  /** LLM models: cost per 1M output tokens. */
  outputTokensPer1M?: number | null;
  currency: string;
}

export interface ProviderCatalogModel {
  id: string;
  displayName: string;
  description?: string | null;
  /** First-release date (ISO 8601). Null when not reliably known. */
  releaseDate?: string | null;
  /** Filter/sort hints — e.g. `"newest"`, `"legacy"`, `"t2i"`, `"i2i"`, `"edit"`. */
  tags?: string[];
  /** Cost at the point of choice. Undefined for free/unpriced models. */
  pricing?: ProviderCatalogPricing | null;
}

/**
 * Single provider entry. `key` is the registry id (lower-case, e.g.
 * `'gemini'`, `'ollama'`, `'fal.ai'`) — that's what `context.provider`
 * accepts on the BE. `available: false` means the provider couldn't
 * be resolved (missing config etc.) — model list will be empty.
 */
export interface ProviderCatalogEntry {
  name: string;
  key: string;
  kind: 'llm' | 'image' | string;
  available: boolean;
  error?: string | null;
  models: ProviderCatalogModel[];
}

export interface ProvidersCatalogResponse {
  llm: ProviderCatalogEntry[];
  image: ProviderCatalogEntry[];
  checkedAt: string;
}

/**
 * One provider's BYOK status. Deliberately carries no key — `lastFour` is a
 * recognition hint so a user can tell which key is stored, and is null when
 * nothing is. There is no endpoint that returns a stored key.
 */
export interface ProviderKeyRow {
  providerId: string;
  hasKey: boolean;
  lastFour?: string | null;
  enabled: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ProviderKeysResponse {
  keys: ProviderKeyRow[];
}

/**
 * Provider health + Ollama tags + catalog surface — corresponds to
 * `ProvidersController` at `/api/v3/providers/*` (B24a-extended).
 */
export const ProviderService = {
  async getHealth(): Promise<ProvidersHealthResponse> {
    return callV3<ProvidersHealthResponse>(apiClient.get('/api/v3/providers/health'));
  },

  async getOllamaInstalledModels(): Promise<OllamaInstalledModelsResponse> {
    return callV3<OllamaInstalledModelsResponse>(apiClient.get('/api/v3/providers/ollama/models'));
  },

  // ---------- BYOK: the caller's own provider keys ----------

  async listKeys(): Promise<ProviderKeysResponse> {
    return callV3<ProviderKeysResponse>(apiClient.get('/api/v3/providers/keys'));
  },

  /** Adds or replaces the key for a provider. The value is never read back. */
  async saveKey(providerId: string, apiKey: string): Promise<ProviderKeyRow> {
    return callV3<ProviderKeyRow>(
      apiClient.put(`/api/v3/providers/keys/${encodeURIComponent(providerId)}`, { apiKey }),
    );
  },

  /** 204 on success — raw axios, since there's no envelope on an empty body. */
  async deleteKey(providerId: string): Promise<void> {
    await apiClient.delete(`/api/v3/providers/keys/${encodeURIComponent(providerId)}`);
  },

  /** Parks or resumes a stored key; parked falls back to the platform key. */
  async setKeyEnabled(providerId: string, enabled: boolean): Promise<ProviderKeyRow> {
    return callV3<ProviderKeyRow>(
      apiClient.patch(`/api/v3/providers/keys/${encodeURIComponent(providerId)}`, { enabled }),
    );
  },

  async getCatalog(): Promise<ProvidersCatalogResponse> {
    return callV3<ProvidersCatalogResponse>(apiClient.get('/api/v3/providers/catalog'));
  },

  /** Animation Lab tier-first model catalog — feeds ModelTierPicker (A-F5). */
  async getVideoCatalog(): Promise<VideoProvidersCatalogResponse> {
    return callV3<VideoProvidersCatalogResponse>(
      apiClient.get('/api/v3/providers/video-catalog'),
    );
  },
};
